/**
 * 모듈의 `getTerrainHeight(lon, lat)` 는 점 하나씩 부르는 형태인데, Cesium 의 메쉬 높이 샘플링은
 * 한 번에 많은 점을 받아야 빠르다. 같은 틱에 들어온 요청을 모아 한 번에 샘플링하고 각자에게 돌려준다.
 */
export type HeightBatchSampler = (points: Array<{ lon: number; lat: number }>) => Promise<Array<number | undefined>>

export interface BatchedHeightSamplerOptions {
  /** 배치를 모으는 시간(ms) */
  windowMs?: number
  /** 한 번에 샘플링할 최대 점 수 */
  maxBatch?: number
  /** 배치 샘플링을 기다리는 최대 시간(ms). 넘으면 그 배치 전체를 폴백 값으로 처리한다. 0 이하면 기다리기만 한다. */
  timeoutMs?: number
  /** 배치가 끝날 때마다 알린다(진단용). */
  onBatch?: (info: { points: number; ms: number; outcome: 'ok' | 'timeout' | 'error' }) => void
}

class SampleTimeoutError extends Error {}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  if (ms <= 0) return promise
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new SampleTimeoutError(`height sampling timed out after ${ms} ms`)), ms)
    promise.then(
      (v) => {
        clearTimeout(id)
        resolve(v)
      },
      (e: unknown) => {
        clearTimeout(id)
        reject(e)
      },
    )
  })
}

/**
 * @param sample   배치 샘플러. 높이를 못 구한 점은 undefined.
 * @param fallback 메쉬에서 못 구했을 때(메쉬 밖 등) 쓸 값. 예: 모듈 기본 지형 높이
 */
export function createBatchedHeightSampler(
  sample: HeightBatchSampler,
  fallback: (lon: number, lat: number) => Promise<number>,
  { windowMs = 8, maxBatch = 4096, timeoutMs = 0, onBatch }: BatchedHeightSamplerOptions = {},
): (lon: number, lat: number) => Promise<number> {
  type Pending = { lon: number; lat: number; resolve: (h: number) => void }
  let queue: Pending[] = []
  let timer: ReturnType<typeof setTimeout> | null = null

  const settle = async (batch: Pending[]) => {
    let heights: Array<number | undefined> = []
    let outcome: 'ok' | 'timeout' | 'error' = 'ok'
    const startedAt = Date.now()
    try {
      heights = await withTimeout(sample(batch.map(({ lon, lat }) => ({ lon, lat }))), timeoutMs)
    } catch (e) {
      outcome = e instanceof SampleTimeoutError ? 'timeout' : 'error'
      heights = []
    }
    onBatch?.({ points: batch.length, ms: Date.now() - startedAt, outcome })
    await Promise.all(
      batch.map(async (p, i) => {
        const h = heights[i]
        if (typeof h === 'number' && Number.isFinite(h)) {
          p.resolve(h)
          return
        }
        try {
          p.resolve(await fallback(p.lon, p.lat))
        } catch {
          p.resolve(0)
        }
      }),
    )
  }

  const flush = () => {
    timer = null
    const pending = queue
    queue = []
    for (let i = 0; i < pending.length; i += maxBatch) {
      void settle(pending.slice(i, i + maxBatch))
    }
  }

  return (lon, lat) =>
    new Promise<number>((resolve) => {
      queue.push({ lon, lat, resolve })
      if (queue.length >= maxBatch) {
        if (timer) clearTimeout(timer)
        flush()
      } else if (!timer) {
        timer = setTimeout(flush, windowMs)
      }
    })
}
